import { Link } from 'react-router-dom';
import type { StorefrontSection } from '@mlm/shared';
import { categoryImageUrl } from '../../../lib/api';
import { useCategories } from '../../lib/hooks';
import { Placeholder } from '../placeholder';

type CategoryGridConfig = Extract<StorefrontSection, { type: 'categoryGrid' }>['config'];

// Static cols→class lookups (Tailwind purges interpolated class names; these are
// full literal strings). The cols=8 default reproduces the former home grid.
const COLS_CLASS: Record<NonNullable<CategoryGridConfig['cols']>, string> = {
  4: 'grid-cols-2 lg:grid-cols-4',
  8: 'grid-cols-4 lg:grid-cols-8',
  10: 'grid-cols-5 lg:grid-cols-10',
};

/**
 * Category tile grid — real categories only; hidden until the admin adds some
 * (same internal gate as before). Extracted verbatim from home.tsx.
 */
export function CategoryGridSection({ section }: { section: StorefrontSection }) {
  const cfg = section.config as CategoryGridConfig;
  const limit = cfg.limit ?? 16;
  const cols = cfg.cols ?? 8;
  const linkBase = cfg.linkBase ?? '/shop?category=';
  const cats = (useCategories().data ?? []).slice(0, limit);
  if (cats.length === 0) return null;

  return (
    <section
      className={`mt-4 grid overflow-hidden rounded-[4px] border-l border-t border-[#ececec] bg-white ${COLS_CLASS[cols]}`}
    >
      {cats.map((c, i) => (
        <Link
          key={c.id}
          to={`${linkBase}${c.id}`}
          className="flex cursor-pointer flex-col items-center gap-2.5 border-b border-r border-[#ececec] p-4 text-center transition-shadow hover:z-10 hover:shadow-[0_6px_16px_rgba(0,0,0,.08)]"
        >
          <span className="h-16 w-16 overflow-hidden rounded-lg">
            <Placeholder i={i} src={c.imageKey ? categoryImageUrl(c.id) : undefined} label="cat" />
          </span>
          <span className="text-xs leading-tight text-[#555]">{c.name}</span>
        </Link>
      ))}
    </section>
  );
}
